import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { useToast } from '../hooks/use-toast';
import { 
  Clock, 
  AlertTriangle, 
  Users, 
  LogOut,
  Timer,
  Lock
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MiroTalkRoom = ({ 
  roomData, 
  userProfile, 
  onLeave, 
  onSessionEnd 
}) => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const iframeRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const sessionStartTimeRef = useRef(null); // Track time when session started
  
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [warningLevel, setWarningLevel] = useState('none');
  const [sessionActive, setSessionActive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [roomUrl, setRoomUrl] = useState(null);

  // Initialize MiroTalk
  useEffect(() => {
    const initializeMiroTalk = async () => {
      if (!roomData || !userProfile) {
        setError('Missing room data or user profile');
        setLoading(false);
        return;
      }

      try {
        console.log('🚀 Initializing MiroTalk P2P room...');
        console.log('Room data:', roomData);
        console.log('User profile:', userProfile);
        
        // Use the stored MiroTalk room URL from roomData
        const miroTalkUrl = roomData.mirotalk_room_url;
        
        if (!miroTalkUrl) {
          throw new Error('No MiroTalk room URL found for this room');
        }

        console.log('✅ Using stored MiroTalk URL:', miroTalkUrl);

        // Set room URL for iframe
        setRoomUrl(miroTalkUrl);

        // Calculate remaining time based on plan and elapsed time
        const maxDuration = roomData.max_duration_minutes;
        const elapsedSeconds = roomData.total_elapsed_seconds || 0;
        
        if (maxDuration && maxDuration > 0) {
          const maxSeconds = maxDuration * 60;
          const remainingSeconds = maxSeconds - elapsedSeconds;
          
          if (remainingSeconds > 0) {
            setTimeRemaining(remainingSeconds);
            sessionStartTimeRef.current = remainingSeconds; // Store starting time for this session
            startTimeTracking(remainingSeconds);
          } else {
            // Time expired
            setError('Session time has been fully used');
            setLoading(false);
            return;
          }
        }

        setSessionActive(true);
        setLoading(false);
        
        toast({
          title: t('connected'),
          description: t('joinedVideoConference'),
          duration: 3000
        });

        console.log('🎉 Successfully initialized MiroTalk meeting');

      } catch (err) {
        console.error('❌ MiroTalk initialization error:', err);
        setError(err.message || 'Failed to connect to video room');
        setLoading(false);
        toast({
          title: t('error'),
          description: err.message || 'Failed to connect to video room. Please try again.',
          variant: 'destructive'
        });
      }
    };

    initializeMiroTalk();

    // Cleanup on unmount
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [roomData, userProfile]);

  const startTimeTracking = useCallback((initialSeconds) => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }

    // Update timer every second
    timerIntervalRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev === null) return null;
        
        const newTime = prev - 1;

        // Check for warnings
        if (newTime <= 300 && newTime > 60) { // 5 minutes
          setWarningLevel('critical');
          if (newTime === 300) {
            showTimeWarning('critical', 5);
          }
        } else if (newTime <= 900 && newTime > 300) { // 15 minutes
          setWarningLevel('warning');
          if (newTime === 900) {
            showTimeWarning('warning', 15);
          }
        }

        // Auto-disconnect when time expires
        if (newTime <= 0) {
          handleTimeExpired();
          return 0;
        }

        return newTime;
      });
    }, 1000);
  }, []);

  const handleTimeExpired = useCallback(async () => {
    console.log('⏰ Time expired - disconnecting');
    
    toast({
      title: t('sessionTimeExpired'),
      description: t('sessionWillEndAutomatically'),
      variant: 'destructive',
      duration: 5000
    });

    // Save elapsed time before disconnecting
    if (sessionStartTimeRef.current !== null) {
      const elapsedInThisSession = sessionStartTimeRef.current;
      
      try {
        await fetch(`${BACKEND_URL}/api/virtual-rooms/${roomData.id}/update-elapsed`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            elapsed_seconds: Math.floor(elapsedInThisSession)
          })
        });
      } catch (error) {
        console.error('Failed to update elapsed time:', error);
      }
    }

    // Give 5 seconds warning then leave
    setTimeout(() => {
      if (onLeave) {
        onLeave();
      }
    }, 5000);

    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }
  }, [t, toast, onLeave, roomData]);

  const showTimeWarning = useCallback((level, minutes) => {
    const title = level === 'critical' ? t('sessionEndingSoon') : t('timeWarning');
    const description = `${minutes} ${t('minutesRemaining')}`;
    
    toast({
      title,
      description,
      variant: level === 'critical' ? 'destructive' : 'default',
      duration: 5000
    });
  }, [t, toast]);

  const handleLeaveRoom = useCallback(async () => {
    console.log('🚪 Leaving room...');
    
    // Calculate elapsed time IN THIS SESSION ONLY
    if (timeRemaining !== null && sessionStartTimeRef.current !== null) {
      const elapsedInThisSession = sessionStartTimeRef.current - timeRemaining;
      
      console.log('Session stats:', {
        startedWith: sessionStartTimeRef.current,
        endingWith: timeRemaining,
        usedInThisSession: elapsedInThisSession
      });
      
      // Only update if we actually used some time
      if (elapsedInThisSession > 0) {
        try {
          const response = await fetch(`${BACKEND_URL}/api/virtual-rooms/${roomData.id}/update-elapsed`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              elapsed_seconds: Math.floor(elapsedInThisSession)
            })
          });
          
          if (response.ok) {
            const result = await response.json();
            console.log('✅ Elapsed time updated:', result);
          }
        } catch (error) {
          console.error('Failed to update elapsed time:', error);
        }
      }
    }
    
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }
    
    if (onLeave) {
      onLeave();
    }
  }, [onLeave, timeRemaining, roomData]);

  const formatTime = (seconds) => {
    if (seconds === null) return t('unlimited');
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    }
    return `${minutes}:${String(secs).padStart(2, '0')}`;
  };

  const getWarningBadgeColor = () => {
    switch (warningLevel) {
      case 'critical': return 'bg-red-500 text-white';
      case 'warning': return 'bg-yellow-500 text-black';
      default: return 'bg-green-500 text-white';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('connecting')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center text-red-600">
            <AlertTriangle className="h-12 w-12 mx-auto mb-4" />
            <p>{error}</p>
            <Button onClick={onLeave} className="mt-4">
              {t('goBack')}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Session Info Header */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">{roomData.title}</CardTitle>
              <div className="flex items-center gap-4 text-sm text-gray-600 mt-2">
                <div className="flex items-center gap-1">
                  <Timer className="h-4 w-4" />
                  <span>{t('plan')}: {(userProfile.subscription_plan || 'basic').toUpperCase()}</span>
                </div>
              </div>
            </div>
            
            {/* Time Remaining Display */}
            {timeRemaining !== null && (
              <div className="text-right">
                <Badge className={`${getWarningBadgeColor()} mb-2`}>
                  <Clock className="h-4 w-4 mr-1" />
                  {formatTime(timeRemaining)} {t('remaining')}
                </Badge>
                {warningLevel !== 'none' && (
                  <div className="flex items-center text-sm text-red-600">
                    <AlertTriangle className="h-4 w-4 mr-1" />
                    {warningLevel === 'critical' ? t('sessionEndingSoon') : t('timeWarning')}
                  </div>
                )}
              </div>
            )}
          </div>
        </CardHeader>
      </Card>

      {/* MiroTalk Video Container */}
      <Card>
        <CardContent className="p-0">
          {roomUrl && (
            <>
              <style>{`
                /* Make MiroTalk close buttons and UI more visible */
                iframe {
                  position: relative;
                  z-index: 1;
                }
                /* Ensure buttons are visible with high contrast */
                .mirotalk-container button {
                  background-color: rgba(255, 255, 255, 0.9) !important;
                  color: #000 !important;
                  border: 2px solid #000 !important;
                }
              `}</style>
              <iframe
                ref={iframeRef}
                src={`${roomUrl}${roomUrl.includes('?') ? '&' : '?'}name=${encodeURIComponent(userProfile.name)}&isPresenter=${roomData.host_id === userProfile.id}`}
                allow="camera; microphone; fullscreen; display-capture; autoplay"
                style={{
                  width: '100%',
                  height: '600px',
                  border: 'none',
                  borderRadius: '8px',
                  backgroundColor: '#1a1a1a'
                }}
                title="MiroTalk Video Conference"
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Control Panel */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Badge variant="outline">
                {sessionActive ? t('sessionActive') : t('sessionInactive')}
              </Badge>
              {roomData.is_private && (
                <Badge variant="secondary">
                  <Lock className="h-3 w-3 mr-1" />
                  {t('privateRoom')}
                </Badge>
              )}
            </div>
            
            <Button 
              onClick={handleLeaveRoom}
              variant="destructive"
              className="flex items-center gap-2"
            >
              <LogOut className="h-4 w-4" />
              {t('leaveRoom')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MiroTalkRoom;
